/**
 * IndexedDB Storage for E2EE Keys and Sessions
 *
 * Stores:
 * - Device keys (identity, signed pre-key, one-time pre-keys)
 * - Ratchet session states per peer
 * - Sender keys for group E2EE
 *
 * Uses idb for promise-based IndexedDB access.
 */

import { openDB, type IDBPDatabase } from "idb";
import { recordCacheHit, recordCacheMiss, recordCacheWrite, recordCacheEvict } from "./cache-telemetry";
import { saveLastKnownDeviceId } from "./rekey";
import { withDeviceKeysLock, notifyDeviceKeysChanged } from "./crypto-tab-sync";

// ============================================================
// Types
// ============================================================

export interface StoredDeviceKeys {
  /** Unique device identifier (client-generated) */
  deviceId: string;
  /** Identity key pair (Curve25519) — private + public */
  identityPrivateKey: string; // base64
  identityPublicKey: string; // base64
  /** Ed25519 signing key pair */
  signingPrivateKey: string; // base64
  signingPublicKey: string; // base64
  /** Signed pre-key */
  signedPreKey: {
    keyId: number;
    privateKey: string; // base64
    publicKey: string; // base64
    signature: string; // base64
  };
  /** Recently-rotated signed pre-keys (newest-first). Optional for backward
   *  compat with bundles stored before SPK-history existed. */
  previousSignedPreKeys?: Array<{
    keyId: number;
    privateKey: string; // base64
    publicKey: string; // base64
    signature: string; // base64
  }>;
  /** One-time pre-keys */
  preKeys: Array<{
    keyId: number;
    privateKey: string; // base64
    publicKey: string; // base64
  }>;
  /** Last-resort / fallback pre-key (Fix 3) — persistent, never consumed. */
  fallbackPreKey?: {
    keyId: number;
    privateKey: string; // base64
    publicKey: string; // base64
  };
  /** When these keys were generated */
  createdAt: string;
}

export interface StoredSession {
  /** Peer user ID */
  peerId: string;
  /** Peer device ID */
  peerDeviceId: string;
  /** Serialized ratchet state */
  ratchetState: string; // JSON serialized SerializedRatchetState
  /** Associated data for AEAD */
  associatedData: string; // base64
  /** Remote identity key */
  remoteIdentityKey: string; // base64
  /** Last updated */
  updatedAt: string;
  /** ISO timestamp of the last successful decrypt from this peer device.
   *  F15d: stale liveness = sender treats the ratchet as dead and forces
   *  fresh X3DH on next send. Phantom devices that never come online
   *  show no peerLastSeen update and get dropped after 7 days. */
  peerLastSeen?: string;
}

export interface StoredSenderKey {
  /** Channel ID this sender key is for */
  channelId: string;
  /** The sender key (AES-256) */
  senderKey: string; // base64
  /** Original seed at iter=0 — never overwritten. Used to re-distribute from the
   *  beginning so a recovering peer can derive ALL historical iterations.
   *  Optional for backward compat: keys generated before this field existed won't
   *  have it; Recovery-redistribution falls back to current ratchet state. */
  initialSeed?: string; // base64
  /** Key ID for rotation tracking */
  keyId: number;
  /** Iteration counter */
  iteration: number;
  /** When this key was created */
  createdAt: string;
  /** Devices that have already received this key, as "userId:deviceId" strings.
   *
   *  H1b: This used to be plain userIds. userId-level tracking silently skipped
   *  newly-added recipient devices (e.g. peer registers a new iPhone) and also
   *  silently skipped peers who lost their IDB state but still present the same
   *  deviceId. Both failure modes produced undecryptable messages with no
   *  proactive re-distribution — only the request-based recovery path could
   *  heal them, and only if the sender was online when the recipient asked.
   *
   *  Now tracked per (userId, deviceId). On every send, the current member set
   *  resolved via POST /users/keys is intersected with distributedTo at the
   *  device level; any device not covered gets a fresh envelope.
   *
   *  Backward compat: old entries may be plain userIds without the ":deviceId"
   *  suffix. Migration logic in useCrypto-group.ts treats any entry without
   *  ":" as stale and drops it, forcing a full re-distribution on the next
   *  send. Safe because re-distribution is idempotent.
   */
  distributedTo?: string[];
}

export interface StoredChannelRecoveryKey {
  /** Channel ID — primary key for the channelRecoveryKeys store. */
  channelId: string;
  /** AES-256 raw recovery key, base64. */
  key: string;
  /** keyId — bumped on rotation. v1 always 1. TODO: rotate on member-leave. */
  keyId: number;
  /** Local creation/import timestamp (ISO). */
  createdAt: string;
}

export interface StoredPeerSenderKey {
  /** channel:userId:deviceId composite key — one slot per SENDER DEVICE, not per user.
   *  Multi-device senders (e.g. Laptop + iPhone) each have their own senderKey;
   *  storing them in the same user-level slot caused ping-pong overwrites and
   *  silent AES-GCM failures when decrypting messages from the "other" device. */
  id: string;
  /** Channel ID */
  channelId: string;
  /** Sender user ID */
  senderId: string;
  /** Sender device ID — the specific device of `senderId` that owns this senderKey.
   *  Derived from the envelope's senderDeviceId at distribute / receive time. */
  senderDeviceId: string;
  /** The sender key (AES-256) */
  senderKey: string; // base64
  /** Seed key at iter=0 — present only when peer distributed us via history-recovery
   *  (fromSeed:true). Enables deterministic decrypt of ANY past iteration by ratcheting
   *  from seed instead of from stored ratchet state. Trade-off: peer history is readable
   *  from this device until seed is rotated/cleared. */
  seed?: string; // base64
  /** Key ID */
  keyId: number;
  /** Last known iteration */
  iteration: number;
  /** Matrix-style frozen ratchet root — set on first receive of a sender key
   *  distribution. Persists for the lifetime of this key (until rotation).
   *  Decrypt computes the per-message key by ratcheting ephemerally from
   *  firstKnownKey over (iter - firstKnownIndex) steps. NEVER mutated on
   *  decrypt. Replaced only when a distribution arrives with a *lower*
   *  iteration (better history coverage). */
  firstKnownIndex?: number;
  /** Ratchet state at firstKnownIndex (base64). Immutable for the key's lifetime. */
  firstKnownKey?: string;
  /** Application-layer replay protection: iterations we have already decrypted
   *  successfully. Bounded to last 1000 entries (most recent kept). */
  seenIters?: number[];
}

// ============================================================
// Database
// ============================================================

const DB_NAME = "kollegeplus-e2ee";
// v5: added optional StoredSession.peerLastSeen field for F15d ratchet-liveness
//     checks. No store schema change — existing records without the field are
//     kept by getOrCreateSession (treated as "no signal", not evicted).
// v6 (F15e): added "channelRecoveryKeys" object store — per-channel AES-256
//     recovery key delivered via the X3DH envelope flow. Enables
//     channel-shared message backup decryption by any member.
// v7: added "messageCache" object store — per-channel message list (last 50)
//     for instant message-list hydration on reload. Best-effort.
// v8: SECURITY — messageCache must hold NO plaintext. Purge any v7 rows (the
//     initial v7 impl stored decrypted plaintext) and recreate the store empty.
//     saveMessageList now strips plaintext before writing.
const DB_VERSION = 8;

let dbPromise: Promise<IDBPDatabase> | null = null;

/** Timeout wrapper — Safari iOS can hang on IndexedDB open after background/sleep */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = withTimeout(
      openDB(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion) {
          // Device keys store (one entry per device)
          if (!db.objectStoreNames.contains("deviceKeys")) {
            db.createObjectStore("deviceKeys", { keyPath: "deviceId" });
          }

          // E2EE sessions store (one per peer+device)
          if (!db.objectStoreNames.contains("sessions")) {
            const sessionStore = db.createObjectStore("sessions", {
              keyPath: ["peerId", "peerDeviceId"],
            });
            sessionStore.createIndex("byPeer", "peerId");
          }

          // Own sender keys for group E2EE
          if (!db.objectStoreNames.contains("senderKeys")) {
            db.createObjectStore("senderKeys", { keyPath: "channelId" });
          }

          // v3 migration: peerSenderKeys key schema changed from `channel:userId`
          // to `channel:userId:deviceId`. Old entries have no senderDeviceId and
          // can't be migrated safely — clear and let recovery (requestSenderKey
          // via the catch-block in decryptGroupMessage) re-populate per-device
          // slots on next decrypt attempt.
          if (oldVersion < 3 && db.objectStoreNames.contains("peerSenderKeys")) {
            db.deleteObjectStore("peerSenderKeys");
          }

          // Peer sender keys for group E2EE (one slot per sender DEVICE)
          if (!db.objectStoreNames.contains("peerSenderKeys")) {
            const peerStore = db.createObjectStore("peerSenderKeys", {
              keyPath: "id",
            });
            peerStore.createIndex("byChannel", "channelId");
            peerStore.createIndex("byChannelSender", ["channelId", "senderId"]);
          }

          // Plaintext cache for own sent messages (v2)
          if (!db.objectStoreNames.contains("plaintextCache")) {
            db.createObjectStore("plaintextCache", { keyPath: "messageId" });
          }

          // v4: added Matrix-style firstKnownIndex/firstKnownKey/seenIters fields
          // to StoredPeerSenderKey (no schema change, fields are optional)
          // v5 (F15d): added optional StoredSession.peerLastSeen field — no
          // schema change required; existing records continue to work and are
          // not evicted on next send (undefined === "no liveness signal yet").

          // v6 (F15e): channel-shared recovery key store. One row per channel,
          // delivered via the existing X3DH envelope flow as
          // envelope.type === "channel_recovery_key".
          if (!db.objectStoreNames.contains("channelRecoveryKeys")) {
            db.createObjectStore("channelRecoveryKeys", { keyPath: "channelId" });
          }

          // v7: per-channel message list for instant reload hydration.
          // Out-of-line keys (keyed by channelId at put/get time), like
          // plaintextCache holds one row per messageId.
          // v8 (SECURITY): the v7 impl stored decrypted plaintext here. Purge any
          // such rows by dropping the store on upgrade from v7, then recreate it
          // empty. saveMessageList strips plaintext from v8 on.
          if (oldVersion === 7 && db.objectStoreNames.contains("messageCache")) {
            db.deleteObjectStore("messageCache");
          }
          if (!db.objectStoreNames.contains("messageCache")) {
            db.createObjectStore("messageCache");
          }
        },
      }),
      5000,
      "IndexedDB open",
    ).catch((err) => {
      // Reset so next call retries
      dbPromise = null;
      throw err;
    });
  }
  return dbPromise;
}

// ============================================================
// Device Keys — localStorage fallback for browsers that clear IDB
// ============================================================

const DEVICE_KEYS_LS_KEY = "kollegeplus:deviceKeys";

function saveDeviceKeysToLS(keys: StoredDeviceKeys): void {
  try {
    localStorage.setItem(DEVICE_KEYS_LS_KEY, JSON.stringify(keys));
  } catch { /* quota exceeded or private mode — non-critical */ }
}

function loadDeviceKeysFromLS(): StoredDeviceKeys | null {
  try {
    const raw = localStorage.getItem(DEVICE_KEYS_LS_KEY);
    return raw ? JSON.parse(raw) as StoredDeviceKeys : null;
  } catch {
    return null;
  }
}

export async function saveDeviceKeys(keys: StoredDeviceKeys): Promise<void> {
  // F22c Task 4: serialise all device-key mutations behind a browser-wide lock
  // (Web Locks when available, in-tab promise-chain mutex otherwise) so
  // concurrent tabs cannot interleave writes.
  //
  // WARNING: do NOT call saveDeviceKeys from inside another withDeviceKeysLock
  // callback — navigator.locks does not support re-entrancy and would deadlock.
  // No current caller nests this call; add a comment if you introduce one.
  await withDeviceKeysLock(async () => {
    // Always save to localStorage first (guaranteed sync, works on Safari iOS)
    saveDeviceKeysToLS(keys);
    // Durable wipe-detection marker under its OWN key — survives an IDB+mirror
    // wipe so init can tell "key-store wiped" from "brand-new device" (see
    // lib/rekey.ts). A full browser-data clear drops this too → correct fallback
    // to the plain new-device flow.
    saveLastKnownDeviceId(keys.deviceId);
    try {
      const db = await getDB();
      await db.put("deviceKeys", keys);
      // Notify sibling tabs that device-keys changed so they reload from IDB
      // rather than publishing stale in-memory state (F22c Task 4). Include the
      // SPK keyId so a sibling on the SAME deviceId still resyncs when the key
      // material rotated (the two-tabs-same-id drift loop).
      notifyDeviceKeysChanged(keys.deviceId, keys.signedPreKey?.keyId);
    } catch (err) {
      console.warn("[crypto-storage] IDB saveDeviceKeys failed (localStorage fallback active):", err);
    }
  });
}

export async function loadDeviceKeys(deviceId: string): Promise<StoredDeviceKeys | undefined> {
  const db = await getDB();
  const fromIDB = await db.get("deviceKeys", deviceId);
  if (fromIDB) return fromIDB;
  // Fallback: check localStorage
  const fromLS = loadDeviceKeysFromLS();
  if (fromLS && fromLS.deviceId === deviceId) {
    // Re-persist to IDB for next time
    await db.put("deviceKeys", fromLS);
    return fromLS;
  }
  return undefined;
}

/** Delete a specific deviceKeys row by deviceId (used by F15b phantom retry). */
export async function deleteDeviceKeysByDeviceId(deviceId: string): Promise<void> {
  try {
    const db = await getDB();
    await db.delete("deviceKeys", deviceId);
  } catch (err) {
    console.warn("[crypto-storage] deleteDeviceKeysByDeviceId failed:", err);
  }
  // Also clear the localStorage mirror if it points to this deviceId
  try {
    const fromLS = loadDeviceKeysFromLS();
    if (fromLS && fromLS.deviceId === deviceId) {
      localStorage.removeItem(DEVICE_KEYS_LS_KEY);
    }
  } catch { /* non-critical */ }
}

export async function getFirstDeviceKeys(): Promise<StoredDeviceKeys | undefined> {
  try {
    const db = await getDB();
    const all = await db.getAll("deviceKeys");
    if (all.length > 0) return all[0];
  } catch (err) {
    console.warn("[crypto-storage] IDB getFirstDeviceKeys failed, using localStorage fallback:", err);
    // IDB unavailable (Safari iOS timeout, private mode, etc.)
  }
  // Fallback: check localStorage
  const fromLS = loadDeviceKeysFromLS();
  if (fromLS) {
    // Try to re-persist to IDB (non-blocking)
    getDB().then((db) => db.put("deviceKeys", fromLS)).catch(() => {});
    return fromLS;
  }
  return undefined;
}

// ============================================================
// Sessions
// ============================================================

export async function saveSession(session: StoredSession): Promise<void> {
  try {
    const db = await getDB();
    await db.put("sessions", session);
  } catch (err) {
    console.warn("[crypto-storage] saveSession failed:", err);
  }
}

/** Like saveSession but propagates IDB errors to the caller instead of swallowing them.
 *  Use on the decrypt critical path where a failed persist must be observable (P0.2). */
export async function saveSessionOrThrow(session: StoredSession): Promise<void> {
  const db = await getDB();
  await db.put("sessions", session);
}

export async function loadSession(
  peerId: string,
  peerDeviceId: string,
): Promise<StoredSession | undefined> {
  try {
    const db = await getDB();
    return db.get("sessions", [peerId, peerDeviceId]);
  } catch (err) {
    console.warn("[crypto-storage] loadSession failed:", err);
    return undefined;
  }
}

export async function loadSessionsForPeer(peerId: string): Promise<StoredSession[]> {
  const db = await getDB();
  return db.getAllFromIndex("sessions", "byPeer", peerId);
}

export async function deleteSession(peerId: string, peerDeviceId: string): Promise<void> {
  const db = await getDB();
  await db.delete("sessions", [peerId, peerDeviceId]);
}

export async function deleteSessionsForPeer(peerId: string): Promise<number> {
  const db = await getDB();
  const sessions = await db.getAllFromIndex("sessions", "byPeer", peerId);
  for (const session of sessions) {
    await db.delete("sessions", [session.peerId, session.peerDeviceId]);
  }
  return sessions.length;
}

// ============================================================
// Sender Keys (Group E2EE)
// ============================================================

export async function saveSenderKey(key: StoredSenderKey): Promise<void> {
  const db = await getDB();
  await db.put("senderKeys", key);
}

export async function loadSenderKey(channelId: string): Promise<StoredSenderKey | undefined> {
  const db = await getDB();
  return db.get("senderKeys", channelId);
}

export async function savePeerSenderKey(
  key: StoredPeerSenderKey,
  opts: { force?: boolean } = {},
): Promise<void> {
  const db = await getDB();
  // Prevent older key from overwriting a newer one (out-of-order delivery).
  // `force` bypass is used for authoritative recovery refreshes (see
  // handleSenderKeyRequest → distributeSenderKey with { recovery: true }) —
  // if the stored state is corrupted/ahead, the guard would otherwise block
  // recovery forever.
  if (!opts.force) {
    const existing = await db.get("peerSenderKeys", key.id);
    if (existing) {
      // Higher keyId always wins; same keyId → higher iteration wins
      if (key.keyId < existing.keyId) return;
      if (key.keyId === existing.keyId && key.iteration < existing.iteration) return;
    }
  }
  await db.put("peerSenderKeys", key);
}

export async function loadPeerSenderKey(
  channelId: string,
  senderId: string,
  senderDeviceId: string,
): Promise<StoredPeerSenderKey | undefined> {
  const db = await getDB();
  return db.get("peerSenderKeys", `${channelId}:${senderId}:${senderDeviceId}`);
}

/** Return ALL per-device sender keys we've stored for a given (channel, sender user).
 *  Used by the proactive prefetch check — "do we already have *any* key from this user?" */
export async function loadPeerSenderKeysByUser(
  channelId: string,
  senderId: string,
): Promise<StoredPeerSenderKey[]> {
  const db = await getDB();
  return db.getAllFromIndex("peerSenderKeys", "byChannelSender", [channelId, senderId]);
}

export async function loadPeerSenderKeysForChannel(
  channelId: string,
): Promise<StoredPeerSenderKey[]> {
  const db = await getDB();
  return db.getAllFromIndex("peerSenderKeys", "byChannel", channelId);
}

/** Replay protection helper — append iter to seenIters, bounded last 1000. */
export function noteSeenIter(key: StoredPeerSenderKey, iter: number): void {
  const seen = key.seenIters ?? [];
  if (seen.includes(iter)) return;
  seen.push(iter);
  if (seen.length > 1000) seen.splice(0, seen.length - 1000);
  key.seenIters = seen;
}

// ============================================================
// Channel Recovery Keys (F15e — Channel-Shared Recovery Key)
// ============================================================

export async function saveChannelRecoveryKey(key: StoredChannelRecoveryKey): Promise<void> {
  try {
    const db = await getDB();
    await db.put("channelRecoveryKeys", key);
  } catch (err) {
    console.warn("[crypto-storage] saveChannelRecoveryKey failed:", err);
  }
}

export async function loadChannelRecoveryKey(
  channelId: string,
): Promise<StoredChannelRecoveryKey | undefined> {
  try {
    const db = await getDB();
    return db.get("channelRecoveryKeys", channelId);
  } catch (err) {
    console.warn("[crypto-storage] loadChannelRecoveryKey failed:", err);
    return undefined;
  }
}

export async function deleteChannelRecoveryKey(channelId: string): Promise<void> {
  try {
    const db = await getDB();
    await db.delete("channelRecoveryKeys", channelId);
  } catch (err) {
    console.warn("[crypto-storage] deleteChannelRecoveryKey failed:", err);
  }
}

// ============================================================
// Secure Erase (Logout)
// ============================================================

export async function eraseAllCryptoData(): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(
    ["deviceKeys", "sessions", "senderKeys", "peerSenderKeys", "plaintextCache", "channelRecoveryKeys"],
    "readwrite",
  );
  await Promise.all([
    tx.objectStore("deviceKeys").clear(),
    tx.objectStore("sessions").clear(),
    tx.objectStore("senderKeys").clear(),
    tx.objectStore("peerSenderKeys").clear(),
    tx.objectStore("plaintextCache").clear(),
    tx.objectStore("channelRecoveryKeys").clear(),
    tx.done,
  ]);
}

/**
 * Stores holding the per-device ratchet/session material that a cascade
 * corruption poisons. Deliberately EXCLUDES `plaintextCache` (decrypted history
 * stays readable) and `channelRecoveryKeys` (the backup key survives).
 */
export const CORRUPT_RATCHET_STORES = [
  "deviceKeys",
  "sessions",
  "senderKeys",
  "peerSenderKeys",
] as const;

/**
 * CR1 cascade auto-recovery: selectively wipe the corrupt ratchet state so the
 * next init mints a fresh device. Keeps cached plaintext + the backup key, so
 * history stays readable. Targeted equivalent of a manual "clear browser data".
 */
export async function clearCorruptRatchetState(): Promise<void> {
  const db = await getDB();
  const tx = db.transaction([...CORRUPT_RATCHET_STORES], "readwrite");
  await Promise.all([
    ...CORRUPT_RATCHET_STORES.map((s) => tx.objectStore(s).clear()),
    tx.done,
  ]);
  // The IDB deviceKeys store is cleared above; also drop the localStorage
  // mirror, else getFirstDeviceKeys() would re-seed the corrupt device on reload.
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(DEVICE_KEYS_LS_KEY);
  }
}

// ============================================================
// Plaintext Cache (own sent messages)
// ============================================================

const PLAINTEXT_CACHE_MAX = 2000;
const PLAINTEXT_CACHE_EVICT_BATCH = 400;

export async function cachePlaintext(messageId: string, plaintext: string): Promise<void> {
  const db = await getDB();
  await db.put("plaintextCache", { messageId, plaintext, cachedAt: Date.now() });
  recordCacheWrite();

  // Periodically evict oldest entries (check every ~50 writes to avoid overhead)
  if (Math.random() < 0.02) {
    const count = await db.count("plaintextCache");
    if (count > PLAINTEXT_CACHE_MAX) {
      const all = await db.getAll("plaintextCache");
      all.sort((a, b) => (a.cachedAt ?? 0) - (b.cachedAt ?? 0));
      const toDelete = all.slice(0, PLAINTEXT_CACHE_EVICT_BATCH);
      const tx = db.transaction("plaintextCache", "readwrite");
      for (const entry of toDelete) {
        tx.store.delete(entry.messageId);
      }
      await tx.done;
      recordCacheEvict(toDelete.length);
    }
  }
}

export async function getCachedPlaintext(messageId: string): Promise<string | undefined> {
  const db = await getDB();
  const entry = await db.get("plaintextCache", messageId);
  if (entry?.plaintext) {
    recordCacheHit();
  } else {
    recordCacheMiss();
  }
  return entry?.plaintext;
}

export async function getCachedPlaintexts(messageIds: string[]): Promise<Map<string, string>> {
  const db = await getDB();
  const result = new Map<string, string>();
  for (const id of messageIds) {
    const entry = await db.get("plaintextCache", id);
    if (entry) result.set(id, entry.plaintext);
  }
  return result;
}

// ============================================================
// Message List Cache (per-channel, for instant reload hydration)
// ============================================================

/**
 * Persist the most recent message list for a channel so a reload can paint the
 * message skeleton instantly before network completes. Best-effort: swallows all
 * errors (cache must never throw into the UI). Caps stored length to last 50.
 *
 * SECURITY: NO plaintext at rest. The `plaintext` field is stripped and replaced
 * with a decrypt placeholder before writing — only ciphertext + metadata are
 * stored. On hydration the skeleton paints, then the decrypt loop fills real text
 * from the in-memory + plaintextCache path. Storing ciphertext is safe (it is the
 * same encrypted blob the server holds).
 */
export async function saveMessageList(channelId: string, messages: unknown[]): Promise<void> {
  try {
    const db = await getDB();
    const sanitized = messages.slice(-50).map((m) => ({
      ...(m as Record<string, unknown>),
      plaintext: "[Nachricht wird entschlüsselt…]",
    }));
    await db.put("messageCache", sanitized, channelId);
  } catch (err) {
    console.warn("[crypto-storage] saveMessageList failed:", err);
  }
}

/** Read the cached message list for a channel. Returns null on miss or error. */
export async function loadMessageList(channelId: string): Promise<unknown[] | null> {
  try {
    const db = await getDB();
    const entry = await db.get("messageCache", channelId);
    return (entry as unknown[]) ?? null;
  } catch {
    return null;
  }
}

/**
 * Remove a single message's plaintext cache entry. Used by DM recovery to
 * invalidate a stale `__NO_BACKUP__` marker before re-decrypting a freshly
 * recovered ciphertext, otherwise the cache short-circuits the new decrypt
 * attempt and the recovered message stays invisible.
 */
export async function deleteCachedPlaintext(messageId: string): Promise<void> {
  const db = await getDB();
  await db.delete("plaintextCache", messageId);
}

/**
 * Completely delete the database (nuclear option)
 */
export async function deleteDatabase(): Promise<void> {
  dbPromise = null;
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}
